import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { PROJECT_TYPES, BUDGETS } from "../data/site";
import { submitQuote } from "../lib/api";

const initial = {
    name: "",
    email: "",
    phone: "",
    project_type: "",
    budget: "",
    location: "",
    message: "",
};

export const QuoteDialog = ({ open, onOpenChange }) => {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.project_type || !form.budget) {
            toast.error("Please complete all required fields.");
            return;
        }
        setLoading(true);
        try {
            await submitQuote(form);
            toast.success("Thanks — your quote request has been received.");
            setForm(initial);
            onOpenChange(false);
        } catch (err) {
            toast.error("Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="quote-dialog"
                className="max-w-2xl bg-[#FAF9F6] border-[#1A1A1A]/15 rounded-none p-0"
            >
                <div className="p-8 md:p-12">
                    <DialogHeader>
                        <div className="overline text-[#8C7C6D] mb-3">
                            Step 01 · Tell us about your project
                        </div>
                        <DialogTitle className="font-serif text-3xl md:text-4xl font-light tracking-tight text-[#1A1A1A]">
                            Request a <span className="italic text-[#8C7C6D]">quote</span>.
                        </DialogTitle>
                    </DialogHeader>

                    <form
                        onSubmit={submit}
                        className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6"
                    >
                        <Input label="Full name *" value={form.name} onChange={set("name")} testid="quote-name" />
                        <Input label="Email *" type="email" value={form.email} onChange={set("email")} testid="quote-email" />
                        <Input label="Phone *" value={form.phone} onChange={set("phone")} testid="quote-phone" />
                        <Input label="Project location" value={form.location} onChange={set("location")} testid="quote-location" />

                        <SelectField
                            label="Project type *"
                            value={form.project_type}
                            onChange={(v) => setForm((f) => ({ ...f, project_type: v }))}
                            options={PROJECT_TYPES}
                            testid="quote-project-type"
                        />
                        <SelectField
                            label="Budget *"
                            value={form.budget}
                            onChange={(v) => setForm((f) => ({ ...f, budget: v }))}
                            options={BUDGETS}
                            testid="quote-budget"
                        />

                        <div className="sm:col-span-2">
                            <label className="dd-label">Message</label>
                            <textarea
                                data-testid="quote-message"
                                rows={4}
                                value={form.message}
                                onChange={set("message")}
                                className="dd-input"
                                placeholder="A few lines about your space, timeline, references..."
                            />
                        </div>

                        <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-2">
                            <button
                                type="button"
                                data-testid="quote-cancel"
                                onClick={() => onOpenChange(false)}
                                className="btn-ghost"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="quote-submit"
                                className="btn-primary disabled:opacity-50"
                            >
                                {loading ? "Submitting..." : "Submit request"}
                            </button>
                        </div>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Input = ({ label, value, onChange, type = "text", testid }) => (
    <div>
        <label className="dd-label">{label}</label>
        <input
            data-testid={testid}
            type={type}
            value={value}
            onChange={onChange}
            className="dd-input"
        />
    </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
    <div>
        <label className="dd-label">{label}</label>
        <select
            data-testid={testid}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="dd-input bg-transparent"
        >
            <option value="">Select...</option>
            {options.map((o) => (
                <option key={o} value={o}>
                    {o}
                </option>
            ))}
        </select>
    </div>
);
